import { useState, useRef } from "react";
import { Badge } from "@/components/ui/badge";
import { Calendar, Save, RotateCcw } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader } from "@/components/ui/card";

const districts = [
  "Bagalkot",
  "Bangalore Rural",
  "Bangalore Urban",
  "Belagavi",
  "Ballari",
  "Bidar",
  "Chamarajanagar",
  "Chikkaballapur",
  "Chikkamagaluru",
  "Chitradurga",
  "Dakshina Kannada",
  "Davanagere",
  "Dharwad",
  "Gadag",
  "Hassan",
  "Haveri",
  "Kalaburagi",
  "Kodagu",
  "Kolar",
  "Koppal",
  "Mandya",
  "Mysuru",
  "Raichur",
  "Ramanagara",
  "Shivamogga",
  "Tumakuru",
  "Udupi",
  "Uttara Kannada",
  "Vijayapura",
  "Yadgir",
];

// Accept userRole as prop (default to "staff" for backward compatibility)
interface RegistrationFormProps {
  setTab?: (tab: string) => void;
  userRole?: "staff" | "admin";
  userName?: string;
  userId?: number;
}
// Always get userRole from localStorage
const RegistrationForm: React.FC<RegistrationFormProps> = (props) => {
  const { setTab, userRole, userName, userId } = props;
  // Use userRole from props
  const effectiveUserRole: "staff" | "admin" = userRole;
  // Helper to show red * for required fields, and 'less than 1MB' for file fields
  const renderLabel = (label: string, required: boolean) => {
    const isFileField =
      label === "Vehicle RC Copy (JPG, PNG, PDF)" ||
      label === "Owner ID Proof (JPG, PNG, PDF)";
    return (
      <Label>
        {label}
        {required && <span style={{ color: "red", marginLeft: 2 }}>*</span>}
        {required && isFileField && (
          <span style={{ color: "#888", marginLeft: 4, fontSize: "0.95em" }}>
            less than 1MB
          </span>
        )}
      </Label>
    );
  };
  // Helper to get today's date in yyyy-mm-dd
  const getToday = () => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
  };
  // Helper to get date one year from today in yyyy-mm-dd
  const getOneYearFromToday = () => {
    const d = new Date();
    d.setFullYear(d.getFullYear() + 1);
    return d.toISOString().slice(0, 10);
  };

  const [formData, setFormData] = useState({
    registration_type: "",
    mt: "10",
    district: "Dakshina Kannada",
    registration_number: "",
    owner_name: "",
    mobile: "",
    alternate_mobile: "",
    imei: "",
    sim_number: "",
    sim_number_bsnl: "",
    sim_start: getToday(),
    sim_end: getOneYearFromToday(),
    rc_copy: null,
    id_proof: null,
  });

  // Determine if the fields should be required
  const isAdmin = effectiveUserRole === "admin";

  // Helper for validation
  const getMissingFields = () => {
    const missing: string[] = [];
    if (!formData.registration_type) missing.push("Registration Type");
    if (!formData.mt) missing.push("MT (Metric Ton)");
    if (!formData.district) missing.push("District");
    if (!formData.registration_number)
      missing.push("Vehicle Registration Number");
    if (!formData.owner_name) missing.push("Vehicle Owner Name");
    // Owner Mobile: exactly 10 digits
    if (!formData.mobile) {
      missing.push("Vehicle Owner Mobile Number");
    } else if (!/^\d{10}$/.test(formData.mobile)) {
      missing.push("Owner Mobile must be exactly 10 digits");
    }
    // Alternate Mobile: only 10 digits
    if (formData.alternate_mobile) {
      if (!/^\d{10}$/.test(formData.alternate_mobile)) {
        missing.push("Alternate Number must be exactly 10 digits");
      }
    }
    if (!formData.rc_copy) missing.push("Vehicle RC Copy");
    if (!formData.id_proof) missing.push("Owner ID Proof");
    // Only required for admin
    if (isAdmin) {
      // IMEI: exactly 15 digits
      if (!formData.imei) {
        missing.push("Device IMEI Number");
      } else if (!/^\d{15}$/.test(formData.imei)) {
        missing.push("IMEI must be exactly 15 digits");
      }
      // SIM Airtel: exactly 13 digits
      if (!formData.sim_number) {
        missing.push("Device SIM Number Airtel");
      } else if (!/^\d{13}$/.test(formData.sim_number)) {
        missing.push("SIM Number Airtel must be exactly 13 digits");
      }
      // SIM BSNL: exactly 13 digits
      if (!formData.sim_number_bsnl) {
        missing.push("Device SIM Number BSNL");
      } else if (!/^\d{13}$/.test(formData.sim_number_bsnl)) {
        missing.push("SIM Number BSNL must be exactly 13 digits");
      }
      if (!formData.sim_start) missing.push("SIM Validity Start Date");
      if (!formData.sim_end) missing.push("SIM Validity End Date");
    }
    return missing;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const missingFields = getMissingFields();

    // For non-admin roles, validate IMEI and SIM fields only if user entered any value
    if (!isAdmin) {
      if (
        formData.imei &&
        formData.imei.length > 0 &&
        formData.imei.length < 15
      ) {
        toast({
          title: "IMEI Format Error",
          description: "IMEI number must be exactly 15 digits.",
          variant: "destructive",
        });
        return;
      }
      if (
        formData.sim_number &&
        formData.sim_number.length > 0 &&
        formData.sim_number.length < 13
      ) {
        toast({
          title: "SIM Format Error",
          description: "SIM number (Airtel) must be exactly 13 digits.",
          variant: "destructive",
        });
        return;
      }
      if (
        formData.sim_number_bsnl &&
        formData.sim_number_bsnl.length > 0 &&
        formData.sim_number_bsnl.length < 13
      ) {
        toast({
          title: "SIM Format Error",
          description: "SIM number (BSNL) must be exactly 13 digits.",
          variant: "destructive",
        });
        return;
      }
    }

    if (missingFields.length > 0) {
      toast({
        title: "Please fill all mandatory fields",
        description: `Missing: ${missingFields.join(", ")}`,
        variant: "destructive",
      });
      return;
    }

    // Determine status
    const allDeviceFieldsFilled =
      formData.imei &&
      formData.sim_number &&
      formData.sim_number_bsnl &&
      formData.sim_start &&
      formData.sim_end;
    const status = allDeviceFieldsFilled ? "Active" : "Pending";

    const form = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      if (value) form.append(key, value as string | Blob);
    });
    form.append("status", status);
    form.append("userId", userId?.toString() || "0");

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/vehicle/registerVehicle`,
        {
          method: "POST",
          credentials: "include",
          body: form,
        }
      );

      const data = await response.json();

      if (response.ok) {
        toast({
          title: "Success",
          description: data.message || "Vehicle registered successfully.",
        });
        handleReset(false);
        if (setTab) setTab(userRole === "admin" ? "dashboard" : "table");
      } else {
        toast({
          title: "Error",
          description: data.error || "Failed to register vehicle.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Submit error:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred.",
        variant: "destructive",
      });
    }
  };

  const handleInputChange = (field: string, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const rcCopyRef = useRef<HTMLInputElement>(null);
  const idProofRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (field: string, file: File | null) => {
    if (file && file.size > 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Please upload a file smaller than 1MB.",
        variant: "destructive",
      });
      // Clear the file input
      if (field === "rc_copy" && rcCopyRef.current)
        rcCopyRef.current.value = "";
      if (field === "id_proof" && idProofRef.current)
        idProofRef.current.value = "";
      setFormData((prev) => ({ ...prev, [field]: null }));
      return;
    }
    setFormData((prev) => ({ ...prev, [field]: file }));
  };

  const handleReset = (showToast: boolean = true) => {
    setFormData({
      registration_type: "",
      mt: "10",
      district: "Dakshina Kannada",
      registration_number: "",
      owner_name: "",
      mobile: "",
      alternate_mobile: "",
      imei: "",
      sim_number: "",
      sim_number_bsnl: "",
      sim_start: getToday(),
      sim_end: getOneYearFromToday(),
      rc_copy: null,
      id_proof: null,
    });
    // Clear file input values
    if (rcCopyRef.current) rcCopyRef.current.value = "";
    if (idProofRef.current) idProofRef.current.value = "";
    if (showToast) {
      toast({
        title: "Form Reset",
        description: "All fields have been cleared.",
      });
    }
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-3xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Vehicle Registration</h1>
          <Badge variant="outline">
            <Calendar className="w-4 h-4 mr-2" />
            {new Date().toLocaleDateString("en-GB")}
          </Badge>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Card className="shadow-lg">
            <CardHeader>
              {/* <CardTitle>Registration Type</CardTitle> */}
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                {renderLabel("Registration Type", true)}
                <Select
                  onValueChange={(value) =>
                    handleInputChange("registration_type", value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select registration type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="New Registration">
                      New Registration
                    </SelectItem>
                    <SelectItem value="Device Replacement">
                      Device Replacement
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                {renderLabel("MT (Metric Ton)", true)}
                <Select
                  onValueChange={(value) => handleInputChange("mt", value)}
                  defaultValue="10"
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select MT" />
                  </SelectTrigger>
                  <SelectContent>
                    {["6", "10", "12", "8", "3", "2", "25", "30", "20"].map(
                      (value) => (
                        <SelectItem key={value} value={value}>
                          {value}
                        </SelectItem>
                      )
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                {renderLabel("District", true)}
                <Select
                  onValueChange={(value) =>
                    handleInputChange("district", value)
                  }
                  defaultValue="Dakshina Kannada"
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select District" />
                  </SelectTrigger>
                  <SelectContent>
                    {districts.map((d) => (
                      <SelectItem key={d} value={d}>
                        {d}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                {renderLabel("Vehicle Registration Number", true)}
                <Input
                  value={formData.registration_number}
                  onChange={(e) =>
                    handleInputChange(
                      "registration_number",
                      e.target.value.toUpperCase()
                    )
                  }
                />
              </div>

              <div className="space-y-2">
                {renderLabel("Vehicle Owner Name", true)}
                <Input
                  value={formData.owner_name}
                  onChange={(e) =>
                    handleInputChange("owner_name", e.target.value)
                  }
                />
              </div>

              <div className="space-y-2">
                {renderLabel("Vehicle Owner Mobile Number", true)}
                <Input
                  type="tel"
                  inputMode="numeric"
                  pattern="\d{10}"
                  maxLength={10}
                  value={formData.mobile}
                  onChange={(e) => {
                    // Only allow digits, max 10
                    let val = e.target.value.replace(/[^\d]/g, "");
                    if (val.length > 10) val = val.slice(0, 10);
                    handleInputChange("mobile", val);
                  }}
                />
              </div>

              <div className="space-y-2">
                {renderLabel(
                  "Vehicle Driver/Owner Alternate Number (Optional)",
                  false
                )}
                <Input
                  type="tel"
                  inputMode="numeric"
                  pattern="\d{10}"
                  maxLength={10}
                  value={formData.alternate_mobile}
                  onChange={(e) => {
                    // Only allow digits, max 10
                    let val = e.target.value.replace(/[^\d]/g, "");
                    if (val.length > 10) val = val.slice(0, 10);
                    handleInputChange("alternate_mobile", val);
                  }}
                />
              </div>

              <div className="space-y-2">
                {renderLabel("Device IMEI Number", isAdmin)}
                <Input
                  inputMode="numeric"
                  maxLength={15}
                  value={formData.imei}
                  onChange={(e) => {
                    // Only allow digits, max 15
                    let val = e.target.value.replace(/[^\d]/g, "");
                    if (val.length > 15) val = val.slice(0, 15);
                    handleInputChange("imei", val);
                  }}
                />
                {formData.imei &&
                  formData.imei.length > 0 &&
                  formData.imei.length < 15 && (
                    <div style={{ color: "red", fontSize: "0.95em" }}>
                      IMEI number must be exactly 15 digits.
                    </div>
                  )}
              </div>

              <div className="space-y-2">
                {renderLabel("Device SIM Number Airtel", isAdmin)}
                <Input
                  inputMode="numeric"
                  maxLength={13}
                  value={formData.sim_number}
                  onChange={(e) => {
                    // Only allow digits, max 13
                    let val = e.target.value.replace(/[^\d]/g, "");
                    if (val.length > 13) val = val.slice(0, 13);
                    handleInputChange("sim_number", val);
                  }}
                />
                {formData.sim_number &&
                  formData.sim_number.length > 0 &&
                  formData.sim_number.length < 13 && (
                    <div style={{ color: "red", fontSize: "0.95em" }}>
                      SIM number (Airtel) must be exactly 13 digits.
                    </div>
                  )}
              </div>

              <div className="space-y-2">
                {renderLabel("Device SIM Number BSNL", isAdmin)}
                <Input
                  inputMode="numeric"
                  maxLength={13}
                  value={formData.sim_number_bsnl}
                  onChange={(e) => {
                    // Only allow digits, max 13
                    let val = e.target.value.replace(/[^\d]/g, "");
                    if (val.length > 13) val = val.slice(0, 13);
                    handleInputChange("sim_number_bsnl", val);
                  }}
                />
                {formData.sim_number_bsnl &&
                  formData.sim_number_bsnl.length > 0 &&
                  formData.sim_number_bsnl.length < 13 && (
                    <div style={{ color: "red", fontSize: "0.95em" }}>
                      SIM number (BSNL) must be exactly 13 digits.
                    </div>
                  )}
              </div>

              <div className="space-y-2">
                {renderLabel("SIM Validity Start Date", isAdmin)}
                <Input
                  type="date"
                  value={formData.sim_start}
                  onChange={(e) =>
                    handleInputChange("sim_start", e.target.value)
                  }
                />
              </div>

              <div className="space-y-2">
                {renderLabel("SIM Validity End Date", isAdmin)}
                <Input
                  type="date"
                  value={formData.sim_end}
                  onChange={(e) => handleInputChange("sim_end", e.target.value)}
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                {renderLabel("Vehicle RC Copy (JPG, PNG, PDF)", true)}
                <Input
                  type="file"
                  accept=".jpg,.jpeg,.png,.pdf"
                  ref={rcCopyRef}
                  onChange={(e) =>
                    handleFileChange("rc_copy", e.target.files?.[0] || null)
                  }
                  onKeyDown={(e) => e.stopPropagation()}
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                {renderLabel("Owner ID Proof (JPG, PNG, PDF)", true)}
                <Input
                  type="file"
                  accept=".jpg,.jpeg,.png,.pdf"
                  ref={idProofRef}
                  onChange={(e) =>
                    handleFileChange("id_proof", e.target.files?.[0] || null)
                  }
                  onKeyDown={(e) => e.stopPropagation()}
                />
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleReset()}
            >
              <RotateCcw className="w-4 h-4 mr-2" /> Reset
            </Button>
            <Button type="submit">
              <Save className="w-4 h-4 mr-2" /> Submit
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegistrationForm;
